import {useTranslation} from '@aboutyou/i18n';
import React, {FC, PropsWithChildren, useEffect, type JSX} from 'react';
import styled from 'styled-components';
import {useOpenedStateManager} from '../../../../../../libraries/opened-state-manager/src/useOpenedStateManager';
import {COLORS} from '../../../constants/colors';
import {Z_INDEXES} from '../../../constants/layers';
import {useLockBodyScroll} from '../../../hooks/useLockBodyScroll/useLockBodyScroll';
import {FLYOUT_PORTAL} from '../../../hooks/usePortalElement/portalIds';
import {Button} from '../../atoms/Buttons/Button';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {OverlayWrapper} from '../../atoms/OverlayWrapper/OverlayWrapper';
import {RenderInPortal} from '../../atoms/RenderInPortal/RenderInPortal';

export interface FullScreenOverlayChildrenProps {
  open: () => void;
  isOpen?: boolean;
}

export const FullScreenOverlay: FC<{
  openStateKey: string;
  isOpenByDefault: boolean;
  dataTestId: string;
  children: (
    params: FullScreenOverlayChildrenProps,
  ) => JSX.Element | React.ReactNode;
  className?: string;
  renderInPortal?: boolean;
}> = ({
  isOpenByDefault,
  dataTestId = 'fullScreenOverlay',
  children,
  openStateKey,
  className,
  renderInPortal,
}) => {
  const {t} = useTranslation();
  const {cancel, isOpen, open} = useOpenedStateManager(openStateKey);

  useLockBodyScroll({lockScroll: isOpen || false});
  useEffect(() => {
    if (isOpenByDefault) {
      open();
    }
  }, []);

  return (
    <PortalFullScreenOverlay isOpen={isOpen} renderInPortal={renderInPortal}>
      <StyledOverlayWrapper
        className={className}
        $isOpen={isOpen}
        data-testid={dataTestId}
      >
        {children({open, isOpen})}
        {isOpen && (
          <StyledCircleCloseButton
            size="sm"
            round
            variant="floating"
            aria-label={t('Close')}
            onClick={cancel}
          >
            <Icon fill={COLORS.Grey1} size="S" asset={GenericIconName.Close} />
          </StyledCircleCloseButton>
        )}
      </StyledOverlayWrapper>
    </PortalFullScreenOverlay>
  );
};

const PortalFullScreenOverlay: FC<
  PropsWithChildren<{isOpen: boolean; renderInPortal?: boolean}>
> = ({isOpen, children, renderInPortal = true}) => {
  if (!isOpen || !renderInPortal) {
    return <>{children}</>;
  }

  return <RenderInPortal portalId={FLYOUT_PORTAL}>{children}</RenderInPortal>;
};

const StyledCircleCloseButton = styled(Button)`
  && {
    position: absolute;
    right: 20px;
    top: 20px;
    z-index: ${Z_INDEXES.TADARIDA_FULLSCREEN_SLIDER_CLOSE_BTN};
  }
`;

const StyledOverlayWrapper = styled(OverlayWrapper)`
  z-index: ${Z_INDEXES.ADP_QUICK_VIEW_POPUP_ON_TOP_FLYOUT};
`;
